import { ProductType } from '../../../../Quotes/types/productTypes';
import { IPType } from '../../../../Quotes/QuoteBuilder/components/Configure/DIAConfig/types/ip';
import Column from '../../../../shared/components/atoms/Column';
import { Amount, Cost } from './Cost';
import { getIPTypeLabel } from '../../../../Quotes/QuoteBuilder/utils/ip';
import React from 'react';
import { IPriceData, IQuote } from '../../../../Quotes/types/store';
import { getAdditionalPrice } from 'Quotes/selectors';
import { TIER_NAMES } from 'Quotes/QuoteBuilder/components/Price/components/DDOSProtection';
import { cloudProviderForDisplay } from 'Order/OrderBuilder/shared/utils/cloudProviderForDisplay';
import { selectIsIndependentRouter } from 'Quotes/QuoteBuilder/components/Configure/DIAConfig/RoutingOption';

function getSelectedPriceData(selectedPrice: IPriceData) {
  const bandwidthCostInstall = selectedPrice.bandwidth_cost?.install ?? 0;
  const bandwidthCostAnnual = selectedPrice.bandwidth_cost?.annual ?? 0;
  const annualIpCharge = selectedPrice.annual_ip_charge ?? 0;
  const mdiaEngineerCost = selectedPrice.mdia_engineer_cost;
  const mdiaRackMountKitCost = selectedPrice.mdia_rack_mount_kit_cost;
  return {
    bandwidthCostInstall,
    bandwidthCostAnnual,
    annualIpCharge,
    mdiaEngineerCost,
    mdiaRackMountKitCost,
  };
}

function DIAConfiguration(props: {
  ipType: IPType | undefined;
  managedDIA: boolean;
  ipAllocation: any;
  amount: number;
}) {
  return (
    <div className="row no-gutters mt-3">
      <Column defaultWidth={2}>
        <span className="sub-heading">DIA Configuration</span>
      </Column>
      <Column defaultWidth={2}>
        <span>{getIPTypeLabel(props.ipType, props.managedDIA, props.ipAllocation)}</span>
      </Column>
      <Column defaultWidth={2} offsetDefaultWidth={2}>
        <span className="mr-2">Annual</span>
        <Amount amount={props.amount} className="annual-ip-cost" />
      </Column>
    </div>
  );
}

function DDoSConfiguration(props: {
  tier: string;
  amount: number;
}) {
  return (
    <div className="row no-gutters mt-3">
      <Column defaultWidth={2}>
        <span className="sub-heading">DDoS Protection</span>
      </Column>
      <Column defaultWidth={2}>
        <span>{TIER_NAMES[props.tier]}</span>
      </Column>
      <Column defaultWidth={2} offsetDefaultWidth={2}>
        <span className="mr-2">Annual</span>
        <Amount amount={props.amount} className="annual-ip-cost" />
      </Column>
    </div>
  );
}

function CloudConnectConfiguration(props: {
  provider: string;
  amount: number;
}) {
  return (
    <div className="row no-gutters mt-3">
      <Column defaultWidth={2}>
        <span className="sub-heading">Cloud Connect</span>
      </Column>
      <Column defaultWidth={2}>
        <span>{props.provider}</span>
      </Column>
      <Column defaultWidth={2} offsetDefaultWidth={2}>
        <span className="mr-2">Annual</span>
        <Amount amount={props.amount} className="annual-ip-cost" />
      </Column>
    </div>
  );
}

export function DiaCosts(props: {
  productType: ProductType;
  ipType: IPType | undefined;
  managedDia: boolean;
  diaIpAllocation: any;
  selectedPrice: IPriceData;
  quote?: IQuote;
  label?: string;
  mdiaAnnualCost: number;
  mdiaRouterCost: number;
  annualIPCharge: number;
}) {
  const ddosProtectionCost = getAdditionalPrice(props.selectedPrice.additional_prices, `DDoS Protection - ${props.quote?.ddosProtection} Cost`)?.annual ?? 0;

  const { bandwidthCostInstall, bandwidthCostAnnual, mdiaEngineerCost, mdiaRackMountKitCost } = getSelectedPriceData(
    props.selectedPrice
  );

  // Determine whether to show IP and Router options based on router type for secondary circuit
  const isPrimaryCircuit = props.label !== 'Secondary';
  const showDIAAddOns = isPrimaryCircuit || (props.quote && props.label === 'Secondary' && !!selectIsIndependentRouter(props.quote));

  return (
    <>
      {props.productType === ProductType.DIA && (
        <div className="row no-gutters mt-3">
          <Column defaultWidth={2}>
            <span className="sub-heading">Bandwidth</span>
          </Column>
          <Column defaultWidth={2} offsetDefaultWidth={2}>
            <span className="mr-2">Install</span>
            <Amount amount={bandwidthCostInstall} className="bandwidth-install" />
          </Column>
          <Column defaultWidth={2}>
            <span className="mr-2">Annual</span>
            <Amount amount={bandwidthCostAnnual} className="bandwidth-annual" />
          </Column>
        </div>
      )}

      {props.productType === ProductType.DIA && (
        <>
          {props.quote?.ddosProtection && showDIAAddOns && (
            <DDoSConfiguration
              tier={props.quote.ddosProtection}
              amount={ddosProtectionCost}
            />
          )}

          {props.quote?.cloud_connect_option?.provider && showDIAAddOns && (
            <CloudConnectConfiguration
              provider={cloudProviderForDisplay(props.quote.cloud_connect_option.provider)}
              amount={props.selectedPrice.cloud_connect?.annual_cost ?? 0}
            />
          )}

          {showDIAAddOns && (
            <DIAConfiguration
              ipType={props.ipType}
              managedDIA={props.managedDia}
              ipAllocation={props.diaIpAllocation}
              amount={props.annualIPCharge}
            />
          )}

          {props.managedDia && (
            <>
              <div className="row no-gutters mt-1">
                <Column defaultWidth={2} offsetDefaultWidth={2}>
                  <span>Managed DIA services</span>
                </Column>
                <Column defaultWidth={2} offsetDefaultWidth={2}>
                  <span className="mr-2">Annual</span>
                  <Amount amount={props.mdiaAnnualCost} className="mdia-annual" />
                </Column>
              </div>
              <div className="row no-gutters mt-1">
                <Column defaultWidth={2} offsetDefaultWidth={2}>
                  <span>Managed DIA router</span>
                </Column>
                <Cost label="Install" cost={props.mdiaRouterCost} className="mdia-install" />
              </div>

              {mdiaEngineerCost && (
                <div className="row no-gutters mt-1">
                  <Column defaultWidth={2} offsetDefaultWidth={2}>
                    <span>Engineer installation</span>
                  </Column>
                  <Cost
                    label="Install"
                    cost={mdiaEngineerCost ?? 0}
                    className="mdia-engineer-install"
                  />
                </div>
              )}

              {mdiaRackMountKitCost && (
                <div className="row no-gutters mt-1">
                  <Column defaultWidth={2} offsetDefaultWidth={2}>
                    <span>Rack mount kit</span>
                  </Column>
                  <Cost
                    label="Install"
                    cost={mdiaRackMountKitCost ?? 0}
                    className="mdia-rack-mount-install"
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
